import { NavLink } from "react-router-dom";
import clsx from "clsx";
import {
  Activity,
  BarChart3,
  ShieldCheck,
  SlidersHorizontal,
  Code2,
  Radar,
  Download,
  Lock,
  ListFilter,
  Map as MapIcon,
} from "lucide-react";
import { EMESRT_COLOR } from "../../lib/format";
import { useAuth } from "../../lib/auth";

import type { Capability } from "../../types/api";

// `cap` gates a link on a capability in the active tenant (undefined = always shown).
const NAV: { to: string; label: string; icon: typeof Activity; cap?: Capability }[] = [
  { to: "/monitor", label: "Monitor", icon: Activity },
  { to: "/cases", label: "Cases", icon: ListFilter },
  { to: "/map", label: "Geospatial", icon: MapIcon },
  { to: "/analyze", label: "Analyze", icon: BarChart3 },
  { to: "/comply", label: "Comply", icon: ShieldCheck, cap: "platform.manage" },
  { to: "/configure", label: "Configure", icon: SlidersHorizontal, cap: "config.write" },
  { to: "/developer", label: "Developer", icon: Code2, cap: "config.write" },
  { to: "/exports", label: "Exports", icon: Download },
];

const EMESRT = [
  { level: "L7", label: "Awareness" },
  { level: "L8", label: "Operability" },
  { level: "L9", label: "Intervention" },
];

/**
 * Sidebar content (branding + nav + EMESRT panel), shared between the fixed
 * desktop rail and the mobile off-canvas drawer. `onNavigate` lets the mobile
 * drawer close itself when a link is tapped.
 */
export function SidebarNav({ onNavigate }: { onNavigate?: () => void }) {
  const { capabilities } = useAuth();
  const has = (cap?: Capability) => !cap || capabilities.includes(cap);
  const canAdmin =
    capabilities.includes("platform.manage") ||
    capabilities.includes("config.write") ||
    capabilities.includes("apikey.manage");
  const nav = [
    ...NAV.filter((n) => has(n.cap)),
    ...(canAdmin ? [{ to: "/admin", label: "Admin", icon: Lock } as const] : []),
  ];
  return (
    <>
      <div className="mb-6 flex items-center gap-2 px-2">
        <div className="relative flex h-8 w-8 items-center justify-center">
          <span className="absolute h-8 w-8 rounded-full border border-aurora-cyan/40 animate-radar" />
          <Radar size={18} className="text-aurora-cyan" />
        </div>
        <div className="leading-tight">
          <div className="font-bold tracking-tight">PacificOne</div>
          <div className="section-tag !tracking-[0.2em]">SAFETY · INTEL</div>
        </div>
      </div>

      <nav className="flex flex-col gap-1">
        {nav.map(({ to, label, icon: Icon }) => (
          <NavLink
            key={to}
            to={to}
            onClick={onNavigate}
            className={({ isActive }) =>
              clsx(
                "flex items-center gap-3 rounded-lg px-3 py-2 text-sm transition",
                isActive
                  ? "bg-aurora-cyan/10 text-aurora-cyan shadow-glow"
                  : "text-slate-300 hover:bg-white/5 hover:text-ink"
              )
            }
          >
            <Icon size={17} />
            {label}
          </NavLink>
        ))}
      </nav>

      <div className="mt-auto">
        <div className="section-tag mb-2 px-2">EMESRT LAYERS</div>
        <div className="space-y-1.5 px-2">
          {EMESRT.map((e) => (
            <div key={e.level} className="flex items-center gap-2 text-xs">
              <span
                className="mono rounded px-1.5 py-0.5 text-[10px] font-semibold"
                style={{ color: EMESRT_COLOR[e.level], background: `${EMESRT_COLOR[e.level]}1a` }}
              >
                {e.level}
              </span>
              <span className="text-slatey">{e.label}</span>
            </div>
          ))}
        </div>
        <div className="mt-4 px-2 text-[10px] leading-relaxed text-slate-600">
          Predict · Prevent · Protect
          <br />
          HSE Pacific × Autobrains.ai
        </div>
      </div>
    </>
  );
}

/** Fixed desktop rail — hidden below `lg`, where the mobile drawer takes over. */
export default function Sidebar() {
  return (
    <aside className="hidden w-[212px] shrink-0 flex-col border-r border-white/5 bg-ocean-900/40 px-3 py-4 lg:flex">
      <SidebarNav />
    </aside>
  );
}
